import { useState, useCallback, useMemo } from 'react';

interface FormState {
    [rpcName: string]: any;
}

interface UseRpcFormStateResult {
    formStates: FormState;
    getFormData: (rpcName: string) => any;
    setFormData: (rpcName: string, data: any) => void;
    clearFormData: (rpcName: string) => void;
    clearAll: () => void;
}

/**
 * Hook to manage form state per RPC tab
 * State persists when switching between tabs
 * 
 * @returns Object with form state management functions
 * 
 * @example
 * const { formStates, getFormData, setFormData } = useRpcFormState();
 * 
 * // Get form data for a specific RPC
 * const submitData = getFormData('submit_simple_spread');
 * 
 * // Update form data
 * setFormData('submit_simple_spread', { strategy: 'test', ... });
 */
export const useRpcFormState = (): UseRpcFormStateResult => {
    const [formStates, setFormStates] = useState<FormState>({});

    const getFormData = useCallback((rpcName: string) => {
        return formStates[rpcName] || null;
    }, [formStates]);

    const setFormData = useCallback((rpcName: string, data: any) => {
        setFormStates(prev => ({
            ...prev,
            [rpcName]: data
        }));
    }, []);

    const clearFormData = useCallback((rpcName: string) => {
        setFormStates(prev => {
            const next = { ...prev };
            delete next[rpcName];
            return next;
        });
    }, []);

    const clearAll = useCallback(() => {
        setFormStates({});
    }, []);

    return useMemo(() => ({
        formStates,
        getFormData,
        setFormData,
        clearFormData,
        clearAll,
    }), [formStates, getFormData, setFormData, clearFormData, clearAll]);
};